import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Hash,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  CheckCircle2,
  Copy,
  AlertCircle,
  MapPin,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNumberWithCommas, parseFormattedNumber } from "@/lib/formatters";
import { BookCondition } from "@/lib/api/book-conditions";
import { ItemStatus } from "@/lib/api/item-statuses";
import { Branch } from "@/lib/api/branches";
import type { Location } from "@/lib/api/locations";

export interface ItemCopyData {
  accessionNumber?: number;
  conditionCode?: string;
  statusCode?: string;
  acquisitionMode?: string;
  locationId?: string;
  branchId?: string;
}

interface CopyDetailsEditorProps {
  copies: number;
  copyData: ItemCopyData[];
  onCopyDataChange: (data: ItemCopyData[]) => void;
  conditions: BookCondition[];
  itemStatuses: ItemStatus[];
  locations?: Location[];
  branches?: Branch[];
  isManualMode: boolean;
  defaultCondition?: string;
  defaultStatus?: string;
  defaultAcquisitionMode?: string;
  defaultLocationId?: string;
  defaultBranchId?: string;
  /** Per-copy: missing acquisition (Add to Catalog validation) */
  highlightCopyAcquisition?: boolean[];
  /** Per-copy: missing / invalid accession in manual mode */
  highlightCopyAccession?: boolean[];
}

export function CopyDetailsEditor({
  copies,
  copyData,
  onCopyDataChange,
  conditions,
  itemStatuses,
  locations = [],
  branches = [],
  isManualMode,
  defaultCondition,
  defaultStatus = "AVAILABLE",
  defaultAcquisitionMode,
  defaultLocationId,
  defaultBranchId,
  highlightCopyAcquisition,
  highlightCopyAccession,
}: CopyDetailsEditorProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCopies, setExpandedCopies] = useState<Set<number>>(new Set());

  // Track bulk apply selections
  const [bulkCondition, setBulkCondition] = useState<string>("none");
  const [bulkStatus, setBulkStatus] = useState<string>(defaultStatus);
  const [bulkAcquisitionMode, setBulkAcquisitionMode] = useState<string>(
    defaultAcquisitionMode || "none"
  );
  const [bulkBranch, setBulkBranch] = useState<string>(defaultBranchId || "none");
  const [bulkLocation, setBulkLocation] = useState<string>(defaultLocationId || "none");

  useEffect(() => {
    setBulkBranch(defaultBranchId || "none");
  }, [defaultBranchId]);

  useEffect(() => {
    setBulkLocation(defaultLocationId || "none");
  }, [defaultLocationId]);

  useEffect(() => {
    const hA = highlightCopyAcquisition;
    const hB = highlightCopyAccession;
    if (copies <= 1) return;
    if (!hA && !hB) return;
    if (!(hA?.some(Boolean) || hB?.some(Boolean))) return;
    setIsExpanded(true);
    setExpandedCopies((prev) => {
      const next = new Set(prev);
      hA?.forEach((v, i) => {
        if (v) next.add(i);
      });
      hB?.forEach((v, i) => {
        if (v) next.add(i);
      });
      return next;
    });
  }, [highlightCopyAcquisition, highlightCopyAccession, copies]);

  // Initialize copy data when copies count changes
  useEffect(() => {
    if (copyData.length !== copies) {
      const newData = Array.from({ length: copies }, (_, i) => ({
        accessionNumber: copyData[i]?.accessionNumber,
        conditionCode: copyData[i]?.conditionCode || defaultCondition,
        statusCode: copyData[i]?.statusCode || defaultStatus,
        acquisitionMode: copyData[i]?.acquisitionMode,
        locationId: copyData[i]?.locationId || defaultLocationId,
        branchId: copyData[i]?.branchId || defaultBranchId,
      }));
      onCopyDataChange(newData);
    }
  }, [copies, defaultCondition, defaultStatus, defaultLocationId, defaultBranchId]);

  const updateCopy = (index: number, field: keyof ItemCopyData, value: any) => {
    const newData = [...copyData];
    if (!newData[index]) {
      newData[index] = {
        conditionCode: defaultCondition,
        statusCode: defaultStatus,
        acquisitionMode: defaultAcquisitionMode,
        locationId: defaultLocationId,
        branchId: defaultBranchId,
      };
    }
    newData[index] = { ...newData[index], [field]: value };
    onCopyDataChange(newData);
  };

  const toggleCopyExpanded = (index: number) => {
    const newExpanded = new Set(expandedCopies);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCopies(newExpanded);
  };

  const applyToAll = (field: keyof ItemCopyData, value: any) => {
    const newData = copyData.map((copy) => ({
      ...copy,
      [field]: value,
    }));
    onCopyDataChange(newData);
  };

  // Check if any copy has missing accession numbers in manual mode
  const hasMissingAccessionNumbers =
    isManualMode && copyData.some((copy) => !copy.accessionNumber || copy.accessionNumber <= 0);

  // Acquisition mode options
  const acquisitionModes = [
    { code: "PURCHASE", label: "Purchase" },
    { code: "DONATION", label: "Donation" },
    { code: "GIFT", label: "Gift" },
    { code: "EXCHANGE", label: "Exchange" },
    { code: "TETFUND", label: "TETFund" },
  ];

  // For single copy, show inline editor
  if (copies === 1) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4 lg:grid-cols-5">
          {/* Accession Number */}
          <div>
            <Label className="flex items-center gap-1.5">
              <Hash className="w-4 h-4 text-gray-400" />
              Accession Number
              {isManualMode && <span className="text-red-500">*</span>}
            </Label>
            <Input
              type="text"
              value={formatNumberWithCommas(copyData[0]?.accessionNumber)}
              onChange={(e) =>
                updateCopy(0, "accessionNumber", parseFormattedNumber(e.target.value))
              }
              placeholder={isManualMode ? "e.g., 12,345" : "Auto-generate"}
              className={cn(
                "mt-1.5 font-mono",
                isManualMode && !copyData[0]?.accessionNumber && "border-red-300",
                highlightCopyAccession?.[0] &&
                  "border-destructive ring-2 ring-destructive/20 border-destructive"
              )}
            />
            {!isManualMode && (
              <p className="mt-1 text-xs text-gray-500">Leave empty to auto-generate</p>
            )}
          </div>

          {/* Condition */}
          <div>
            <Label className="flex items-center gap-1.5">
              <ClipboardCheck className="w-4 h-4 text-gray-400" />
              Condition
              <span className="text-red-500">*</span>
            </Label>
            <Select
              value={copyData[0]?.conditionCode || "none"}
              onValueChange={(value) =>
                updateCopy(0, "conditionCode", value === "none" ? undefined : value)
              }
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder="Select condition" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {conditions.map((condition) => (
                  <SelectItem key={condition.code} value={condition.code}>
                    {condition.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div>
            <Label className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-gray-400" />
              Status
              <span className="text-red-500">*</span>
            </Label>
            <Select
              value={copyData[0]?.statusCode || "AVAILABLE"}
              onValueChange={(value) => updateCopy(0, "statusCode", value)}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {itemStatuses.map((status) => (
                  <SelectItem key={status.code} value={status.code}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Acquisition Mode */}
          <div>
            <Label className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-gray-400" />
              Acquisition Mode
              <span className="text-red-500">*</span>
            </Label>
            <Select
              value={copyData[0]?.acquisitionMode || "none"}
              onValueChange={(value) =>
                updateCopy(0, "acquisitionMode", value === "none" ? undefined : value)
              }
            >
              <SelectTrigger
                className={cn(
                  "mt-1.5",
                  highlightCopyAcquisition?.[0] &&
                    "border-destructive ring-2 ring-destructive/20 border-destructive"
                )}
              >
                <SelectValue placeholder="Select mode" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {acquisitionModes.map((mode) => (
                  <SelectItem key={mode.code} value={mode.code}>
                    {mode.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Location */}
          {locations.length > 0 && (
            <div>
              <Label className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-gray-400" />
                Location
                <span className="text-red-500">*</span>
              </Label>
              <Select
                value={copyData[0]?.locationId || "none"}
                onValueChange={(value) =>
                  updateCopy(0, "locationId", value === "none" ? undefined : value)
                }
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No location</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Branch */}
          {branches.length > 0 && (
            <div>
              <Label className="flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4 text-gray-400" />
                Branch
              </Label>
              <Select
                value={copyData[0]?.branchId || "none"}
                onValueChange={(value) =>
                  updateCopy(0, "branchId", value === "none" ? undefined : value)
                }
              >
                <SelectTrigger className="mt-1.5">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No branch</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    );
  }

  // For multiple copies, show collapsible editor
  return (
    <div className="space-y-4">
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "gap-2",
                hasMissingAccessionNumbers && "border-red-300 text-red-600 hover:bg-red-50"
              )}
            >
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <Copy className="w-4 h-4" />
              Configure {copies} Copies Individually
              {hasMissingAccessionNumbers && <AlertCircle className="w-4 h-4 text-red-500" />}
            </Button>
          </CollapsibleTrigger>

          {isManualMode && !isExpanded && (
            <p className="text-sm text-amber-600">
              Manual mode: Expand to enter accession numbers for each copy
            </p>
          )}
        </div>

        <CollapsibleContent className="mt-4 space-y-3">
          {/* Bulk Apply Options */}
          <Card className="p-3 bg-gray-50">
            <p className="text-sm font-medium text-gray-700 mb-2">Apply to all copies:</p>
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">
                  Condition:
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <Select
                  value={bulkCondition}
                  onValueChange={(value) => {
                    setBulkCondition(value);
                    applyToAll("conditionCode", value === "none" ? undefined : value);
                  }}
                >
                  <SelectTrigger className="w-40 h-8">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {conditions.map((condition) => (
                      <SelectItem key={condition.code} value={condition.code}>
                        {condition.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">
                  Status:
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <Select
                  value={bulkStatus}
                  onValueChange={(value) => {
                    setBulkStatus(value);
                    applyToAll("statusCode", value);
                  }}
                >
                  <SelectTrigger className="w-40 h-8">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {itemStatuses.map((status) => (
                      <SelectItem key={status.code} value={status.code}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">
                  Acquisition:
                  <span className="text-red-500 ml-1">*</span>
                </Label>
                <Select
                  value={bulkAcquisitionMode}
                  onValueChange={(value) => {
                    setBulkAcquisitionMode(value);
                    applyToAll("acquisitionMode", value === "none" ? undefined : value);
                  }}
                >
                  <SelectTrigger className="w-40 h-8">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {acquisitionModes.map((mode) => (
                      <SelectItem key={mode.code} value={mode.code}>
                        {mode.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {locations.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">
                    Location:
                    <span className="text-red-500 ml-1">*</span>
                  </Label>
                  <Select
                    value={bulkLocation}
                    onValueChange={(value) => {
                      setBulkLocation(value);
                      applyToAll("locationId", value === "none" ? undefined : value);
                    }}
                  >
                    <SelectTrigger className="w-44 h-8">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No location</SelectItem>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {branches.length > 0 && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-gray-600">Branch:</Label>
                  <Select
                    value={bulkBranch}
                    onValueChange={(value) => {
                      setBulkBranch(value);
                      applyToAll("branchId", value === "none" ? undefined : value);
                    }}
                  >
                    <SelectTrigger className="w-44 h-8">
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No branch</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </Card>

          {/* Individual Copy Editors */}
          <div className="space-y-2">
            {Array.from({ length: copies }, (_, index) => {
              const copy = copyData[index] || {};
              const isCopyExpanded = expandedCopies.has(index);
              const hasMissingAccession =
                isManualMode && (!copy.accessionNumber || copy.accessionNumber <= 0);

              return (
                <Card
                  key={index}
                  className={cn(
                    "overflow-hidden",
                    hasMissingAccession && "border-red-200 bg-red-50/50"
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50",
                      isCopyExpanded && "border-b bg-gray-50"
                    )}
                    onClick={() => toggleCopyExpanded(index)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-gray-700">Copy {index + 1}</span>
                      {copy.accessionNumber ? (
                        <span className="text-sm text-gray-500">
                          Accession:{" "}
                          <span className="font-mono">
                            {formatNumberWithCommas(copy.accessionNumber)}
                          </span>
                        </span>
                      ) : isManualMode ? (
                        <span className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          Accession number required
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">Auto-generate</span>
                      )}
                    </div>
                    {isCopyExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </div>

                  {isCopyExpanded && (
                    <div className="p-3 grid grid-cols-1 gap-3 md:grid-cols-4 lg:grid-cols-5">
                      {/* Accession Number */}
                      <div>
                        <Label className="text-sm">
                          Accession Number
                          {isManualMode && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <Input
                          type="text"
                          value={formatNumberWithCommas(copy.accessionNumber)}
                          onChange={(e) =>
                            updateCopy(
                              index,
                              "accessionNumber",
                              parseFormattedNumber(e.target.value)
                            )
                          }
                          placeholder={isManualMode ? "e.g., 12,345" : "Auto"}
                          className={cn(
                            "mt-1 font-mono",
                            hasMissingAccession && "border-red-300",
                            highlightCopyAccession?.[index] &&
                              "border-destructive ring-2 ring-destructive/20 border-destructive"
                          )}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      {/* Condition */}
                      <div>
                        <Label className="text-sm">
                          Condition
                          <span className="text-red-500 ml-1">*</span>
                        </Label>
                        <Select
                          value={copy.conditionCode || "none"}
                          onValueChange={(value) =>
                            updateCopy(index, "conditionCode", value === "none" ? undefined : value)
                          }
                        >
                          <SelectTrigger className="mt-1" onClick={(e) => e.stopPropagation()}>
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {conditions.map((condition) => (
                              <SelectItem key={condition.code} value={condition.code}>
                                {condition.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Status */}
                      <div>
                        <Label className="text-sm">
                          Status
                          <span className="text-red-500 ml-1">*</span>
                        </Label>
                        <Select
                          value={copy.statusCode || "AVAILABLE"}
                          onValueChange={(value) => updateCopy(index, "statusCode", value)}
                        >
                          <SelectTrigger className="mt-1" onClick={(e) => e.stopPropagation()}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {itemStatuses.map((status) => (
                              <SelectItem key={status.code} value={status.code}>
                                {status.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Acquisition Mode */}
                      <div>
                        <Label className="text-sm">
                          Acquisition
                          <span className="text-red-500 ml-1">*</span>
                        </Label>
                        <Select
                          value={copy.acquisitionMode || "none"}
                          onValueChange={(value) =>
                            updateCopy(
                              index,
                              "acquisitionMode",
                              value === "none" ? undefined : value
                            )
                          }
                        >
                          <SelectTrigger
                            className={cn(
                              "mt-1",
                              highlightCopyAcquisition?.[index] &&
                                "border-destructive ring-2 ring-destructive/20 border-destructive"
                            )}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            {acquisitionModes.map((mode) => (
                              <SelectItem key={mode.code} value={mode.code}>
                                {mode.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Location */}
                      {locations.length > 0 && (
                        <div>
                          <Label className="text-sm">
                            Location
                            <span className="text-red-500 ml-1">*</span>
                          </Label>
                          <Select
                            value={copy.locationId || "none"}
                            onValueChange={(value) =>
                              updateCopy(index, "locationId", value === "none" ? undefined : value)
                            }
                          >
                            <SelectTrigger className="mt-1" onClick={(e) => e.stopPropagation()}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No location</SelectItem>
                              {locations.map((loc) => (
                                <SelectItem key={loc.id} value={loc.id}>
                                  {loc.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Branch */}
                      {branches.length > 0 && (
                        <div>
                          <Label className="text-sm">Branch</Label>
                          <Select
                            value={copy.branchId || "none"}
                            onValueChange={(value) =>
                              updateCopy(index, "branchId", value === "none" ? undefined : value)
                            }
                          >
                            <SelectTrigger className="mt-1" onClick={(e) => e.stopPropagation()}>
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No branch</SelectItem>
                              {branches.map((branch) => (
                                <SelectItem key={branch.id} value={branch.id}>
                                  {branch.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Warning for manual mode when not expanded */}
      {isManualMode && !isExpanded && hasMissingAccessionNumbers && (
        <p className="text-sm text-red-600 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          Accession numbers are required for all copies in manual mode. Expand to configure.
        </p>
      )}
    </div>
  );
}
